import React, { useRef } from 'react'
import PropTypes from 'prop-types'

import MetaData from '@aok/meta'
import { StructuredDataArticle } from '@aok/structured-data'
import { HeadlineLevel } from '@aok/text-elements'

import Comment from '../../atoms/Comment/Comment'
import CookieBanner from '../../organisms/CookieBanner/CookieBanner'
import ArticleHeader from '../../organisms/ArticleHeader/ArticleHeader'
import ArticleFooter from '../../organisms/ArticleFooter/ArticleFooter'
import FeedbackForm from '../../organisms/FeedbackForm/FeedbackForm'
import MagazineHeader from '../../organisms/MagazineHeader/MagazineHeader'
import MagazineFooter from '../../organisms/MagazineFooter/MagazineFooter'
import Y008 from '../../organisms/M.Y008/Y008'
import Y004S from '../../organisms/M.Y004/Y004-S'
import Y015 from '../../organisms/M.Y015/Y015'
import Survey from '../../molecules/Survey/Survey'
import Spacer from '../Spacer'

import { clientListPropTypes, footerPropTypes } from '../../../utils/commonPropTypes'
import CombinedContextProvider from '../../../context/CombinedContextProvider'
import Y018 from '../../organisms/M.Y018/Y018'
import T001 from "../../organisms/M.T001/T001";
import { getUnprefixedPathname } from '../../../utils/UrlUtils'

const ArticleLayout = ({
  apis,
  children,
  clientList,
  feedbackForm,
  footer,
  header,
  lastUpdated,
  meta,
  pageHeader,
  privacy,
  readingTime,
  releaseDate,
  author,
  sources,
  structuredData,
  surveyEnabled,
  tags,
  y008,
  y013,
  y014,
  y015,
  y018,
}) => {
  const headerRef = useRef(null)
  const mainContentRef = useRef(null)
  let unprefixedPathname = typeof window !== 'undefined' ? getUnprefixedPathname(window.location.pathname) : null
  const urlCheck = [
    'magazin/koerper-psyche/stoffwechsel/was-fuer-ein-koerperfettanteil-gesund-ist-und-wie-man-ihn-berechnet',
    'magazin/ernaehrung/ernaehrungsformen/wie-gesund-sind-keto-diaeten',
    'magazin/ernaehrung/abnehmen/abnehmen-mit-der-eiweiss-diaet-viel-protein-und-low-carb',
    'magazin/koerper-psyche/muskel-skelett-system/tipps-fuer-den-muskelaufbau',
    'magazin/ernaehrung/abnehmen/wie-kann-ich-abnehmen-tipps-fuer-ernaehrung-und-bewegung',
    'magazin/koerper-psyche/haut-und-allergie/warum-es-bei-maennern-zum-haarausfall-kommt',
    'magazin/ernaehrung/gesunde-ernaehrung/welche-spurenelemente-brauchen-wir',
    'magazin/sport/workout/zu-welcher-zeit-ist-das-sporttraining-am-effektivsten',
  ];
  const contains = unprefixedPathname ? urlCheck.some(sub => unprefixedPathname.includes(sub)) : null;

  return (
    <CombinedContextProvider apis={apis} privacy={privacy} clientList={clientList}>
      <StructuredDataArticle {...structuredData} />

      <MetaData pageType="article" {...meta} />

      <CookieBanner />

      <T001 />

      {/*{surveyEnabled && <Survey />}*/}
      {surveyEnabled && contains && <Survey />}

      <MagazineHeader headerRef={headerRef} hasScrollIndicator={true} mainContentRef={mainContentRef} {...header} />

      <main
        id="main-content"
        className="article
          overflow-hidden flex-grow flex-shrink-0
          bg-ffffff
          pt-64 600:pt-80 1280:pt-96
        "
        ref={mainContentRef}
      >
        <ArticleHeader
          readingTime={readingTime}
          releaseDate={releaseDate}
          lastUpdated={lastUpdated}
          author={author}
          {...pageHeader}
        />

        <HeadlineLevel>{children}</HeadlineLevel>

        {(tags || sources) && <ArticleFooter tags={tags} sources={sources} />}
      </main>

      <Comment text="googleoff: all" />

      {feedbackForm && (
        <HeadlineLevel>
          <Spacer className="h-64 720:h-80 1000:h-112" />
          <FeedbackForm {...feedbackForm} />
        </HeadlineLevel>
      )}

      {y013 && (
        <HeadlineLevel>
          <Spacer className="h-64 720:h-80 1000:h-112" />
          <Y004S dataTrackingName="Y013" {...y013} settings={{ colorScheme: 'structure' }} />
        </HeadlineLevel>
      )}

      {y014 && (
        <HeadlineLevel>
          <Spacer className="h-64 720:h-80 1000:h-112" />
          <Y004S dataTrackingName="Y014" {...y014} />
        </HeadlineLevel>
      )}

      {y015 && (
        <HeadlineLevel>
          <Spacer className="h-64 720:h-80 1000:h-112" />
          <Y015 {...y015} />
          <Spacer className="h-64 720:h-80 1000:h-112" />
        </HeadlineLevel>
      )}

      {y018 && (
        <HeadlineLevel>
          <Spacer className="h-64 720:h-80 1000:h-112" />
          <Y018 {...y018} />
          <Spacer className="h-64 720:h-80 1000:h-112" />
        </HeadlineLevel>
      )}

      {y008 && (
        <HeadlineLevel>
          <Y008 {...y008} />
        </HeadlineLevel>
      )}

      <MagazineFooter {...footer} privacy={privacy} />
    </CombinedContextProvider>
  )
}

ArticleLayout.propTypes = {
  children: PropTypes.node,
  apis: PropTypes.object,
  meta: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    aokTitle: PropTypes.string,
    aokDescription: PropTypes.string,
    keywords: PropTypes.array,
  }),
  header: PropTypes.shape({
    mainMenu: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      })
    ),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      })
    ),
  }),
  footer: PropTypes.shape(footerPropTypes),
  privacy: PropTypes.shape({
    socialMedia: PropTypes.shape({
      headline: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
    cookie: PropTypes.shape({
      text: PropTypes.string.isRequired,
      linkText: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  }),
  clientList: clientListPropTypes,
  structuredData: PropTypes.shape({
    image: PropTypes.string,
    headline: PropTypes.string,
    datePublished: PropTypes.string,
    dateModified: PropTypes.string,
    keywords: PropTypes.arrayOf(PropTypes.string),
    publisher: PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string,
      legalName: PropTypes.string,
      url: PropTypes.string,
      sameAs: PropTypes.arrayOf(PropTypes.string),
    }),
    author: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        image: PropTypes.shape({
          url: PropTypes.string,
          alt: PropTypes.string,
        }),
        profile: PropTypes.shape({
          url: PropTypes.string,
          title: PropTypes.string,
        }),
      })
    ),
  }),
  author: PropTypes.shape({
    name: PropTypes.string,
  }),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  sources: PropTypes.string,
  y008: PropTypes.object,
  y013: PropTypes.object,
  y014: PropTypes.object,
  y015: PropTypes.object,
  y018: PropTypes.object,
}

ArticleLayout.defaultProps = {
  children: '',
  meta: {
    title: 'Artikel',
    description: 'Standard Artikel Template ohne spezifische Beschreibung',
    keywords: ['Lorem', 'Ipsum'],
  },
  privacy: {},
  structuredData: {},
}

export default ArticleLayout
